import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const courseOptions = [
  "Bachelors of Hotel Management and Catering Technology",
  "B.Sc in Hotel Management",
  "Diploma in Culinary Arts",
  "International Diploma in Hotel Management",
  "Diploma in Bartending",
  "Certificate in Bar Management / Mixology",
  "Integrated Program (5 Years)",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    course_interested: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Full name is required";
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Please enter a valid email";
    }
    if (!form.phone.trim()) {
      errs.phone = "Phone number is required";
    } else if (!/^[0-9+\-() ]{7,15}$/.test(form.phone)) {
      errs.phone = "Please enter a valid phone number";
    }
    if (!form.course_interested) errs.course_interested = "Please select a course";
    if (!form.message.trim()) errs.message = "Message is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ type: "success", message: "Your inquiry has been submitted successfully! We will get back to you shortly." });
      setForm({ full_name: "", email: "", phone: "", course_interested: "", message: "" });
    } catch {
      setStatus({ type: "error", message: "Something went wrong. Please try again later." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Banner */}
      <section className="relative pt-36 pb-20 bg-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <p className="font-body text-sm uppercase tracking-[0.3em] text-white/70 mb-3">Contact</p>
          <h1 data-testid="contact-hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Get in Touch with Vainavi
          </h1>
          <p className="font-body text-lg text-white/80 max-w-2xl">
            Have questions about our programs? We are here to help you take the first step towards your hospitality career.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section data-testid="contact-info-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: MapPin, title: "Address", text: "M.S.P-6, 11-5-150/C, Moosapet Rd, Bhavani Nagar, Moosapet, Hyderabad, Telangana 500018" },
              { icon: Phone, title: "Phone", text: "+91 99637 56669" },
              { icon: Mail, title: "Email", text: "Vainavicollege@gmail.com" },
              { icon: Clock, title: "Office Hours", text: "Mon - Sat: 9:00 AM - 5:00 PM" },
            ].map((info, i) => (
              <div
                key={info.title}
                data-testid={`contact-info-card-${i}`}
                className="p-6 bg-slate-50 border-l-4 border-crimson"
              >
                <info.icon className="w-6 h-6 text-crimson mb-3" />
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{info.title}</h3>
                <p className="font-body text-sm text-slate-600">{info.text}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-2">Send Us a Message</h2>
              <p className="font-body text-sm text-slate-500 mb-8">Fill out the form below and our admissions team will respond within 24 hours.</p>

              {status.message && (
                <div
                  data-testid="form-status-message"
                  className={`flex items-start gap-3 p-4 mb-6 ${
                    status.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  {status.type === "success" ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  <p className="font-body text-sm">{status.message}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block font-body text-sm font-semibold text-navy mb-1.5">Full Name *</label>
                  <input
                    data-testid="contact-fullname-input"
                    type="text"
                    name="full_name"
                    value={form.full_name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={`flex h-12 w-full border bg-white px-4 py-2 font-body text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 ${
                      errors.full_name ? "border-red-400" : "border-slate-200"
                    }`}
                  />
                  {errors.full_name && <p className="font-body text-xs text-red-500 mt-1">{errors.full_name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block font-body text-sm font-semibold text-navy mb-1.5">Email Address *</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className={`flex h-12 w-full border bg-white px-4 py-2 font-body text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 ${
                      errors.email ? "border-red-400" : "border-slate-200"
                    }`}
                  />
                  {errors.email && <p className="font-body text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block font-body text-sm font-semibold text-navy mb-1.5">Phone Number *</label>
                  <input
                    data-testid="contact-phone-input"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className={`flex h-12 w-full border bg-white px-4 py-2 font-body text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 ${
                      errors.phone ? "border-red-400" : "border-slate-200"
                    }`}
                  />
                  {errors.phone && <p className="font-body text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>

                {/* Course Interested In */}
                <div>
                  <label className="block font-body text-sm font-semibold text-navy mb-1.5">Course Interested In *</label>
                  <select
                    data-testid="contact-course-select"
                    name="course_interested"
                    value={form.course_interested}
                    onChange={handleChange}
                    className={`flex h-12 w-full border bg-white px-4 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 ${
                      errors.course_interested ? "border-red-400" : "border-slate-200"
                    } ${!form.course_interested ? "text-slate-400" : "text-slate-900"}`}
                  >
                    <option value="">Select a course</option>
                    {courseOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {errors.course_interested && <p className="font-body text-xs text-red-500 mt-1">{errors.course_interested}</p>}
                </div>

                {/* Message */}
                <div>
                  <label className="block font-body text-sm font-semibold text-navy mb-1.5">Message *</label>
                  <textarea
                    data-testid="contact-message-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your interests and any questions you have..."
                    className={`flex w-full border bg-white px-4 py-3 font-body text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 resize-none ${
                      errors.message ? "border-red-400" : "border-slate-200"
                    }`}
                  />
                  {errors.message && <p className="font-body text-xs text-red-500 mt-1">{errors.message}</p>}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-crimson text-white px-10 py-3.5 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Inquiry
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Google Maps */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-2">Find Us</h2>
              <p className="font-body text-sm text-slate-500 mb-6">Visit our campus — view the exact location on Google Maps below.</p>
              <div data-testid="google-map" className="w-full h-[480px] border border-slate-200">
                <iframe
                  title="Vainavi Educational Institutions Location"
                  src="https://maps.google.com/maps?q=Vainavi+Institute+of+Hotel+Management+Hyderabad&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <a
                href="https://maps.app.goo.gl/Kn2Xwjruwma2q3rV8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 font-body text-sm font-semibold text-crimson hover:underline"
              >
                <MapPin className="w-4 h-4" />
                View on Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
